/**
 * Amend Metadata Tab
 *
 * Tab component for editing study metadata in the amend flow
 */

import { Input } from '@/presentation/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import type { Study, AmendStudyData } from '@/domain/study/types'

interface AmendMetadataTabProps {
  study: Study
  metadata: NonNullable<AmendStudyData['metadata']>
  onChange: (metadata: AmendStudyData['metadata']) => void
}

export function AmendMetadataTab({ study, metadata, onChange }: AmendMetadataTabProps) {
  const handleChange = (field: keyof NonNullable<AmendStudyData['metadata']>, value: string) => {
    onChange({ ...metadata, [field]: value })
  }

  const isChanged = (field: keyof Study, newValue: string | undefined) => {
    const original = study[field] || ''
    return newValue !== original
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Metadatos del Estudio</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            Descripcion del estudio
            {isChanged('studyDescription', metadata.studyDescription) && (
              <span className="text-blue-600 dark:text-blue-400 text-xs ml-2">(modificado)</span>
            )}
          </label>
          <Input
            value={metadata.studyDescription || ''}
            onChange={(e) => handleChange('studyDescription', e.target.value)}
            placeholder="Ej: TC de Torax con contraste"
          />
          {study.studyDescription && isChanged('studyDescription', metadata.studyDescription) && (
            <p className="text-xs text-[var(--foreground-muted)] mt-1">
              Valor anterior: {study.studyDescription}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Medico remitente
            {isChanged('referringPhysician', metadata.referringPhysician) && (
              <span className="text-blue-600 dark:text-blue-400 text-xs ml-2">(modificado)</span>
            )}
          </label>
          <Input
            value={metadata.referringPhysician || ''}
            onChange={(e) => handleChange('referringPhysician', e.target.value)}
            placeholder="Nombre del medico remitente"
          />
          {study.referringPhysician && isChanged('referringPhysician', metadata.referringPhysician) && (
            <p className="text-xs text-[var(--foreground-muted)] mt-1">
              Valor anterior: {study.referringPhysician}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Medico ejecutante
            {isChanged('performingPhysician', metadata.performingPhysician) && (
              <span className="text-blue-600 dark:text-blue-400 text-xs ml-2">(modificado)</span>
            )}
          </label>
          <Input
            value={metadata.performingPhysician || ''}
            onChange={(e) => handleChange('performingPhysician', e.target.value)}
            placeholder="Nombre del medico ejecutante"
          />
          {study.performingPhysician && isChanged('performingPhysician', metadata.performingPhysician) && (
            <p className="text-xs text-[var(--foreground-muted)] mt-1">
              Valor anterior: {study.performingPhysician}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Parte del cuerpo
            {isChanged('bodyPartExamined', metadata.bodyPartExamined) && (
              <span className="text-blue-600 dark:text-blue-400 text-xs ml-2">(modificado)</span>
            )}
          </label>
          <Input
            value={metadata.bodyPartExamined || ''}
            onChange={(e) => handleChange('bodyPartExamined', e.target.value)}
            placeholder="Ej: CHEST, HEAD, ABDOMEN"
          />
          {study.bodyPartExamined && isChanged('bodyPartExamined', metadata.bodyPartExamined) && (
            <p className="text-xs text-[var(--foreground-muted)] mt-1">
              Valor anterior: {study.bodyPartExamined}
            </p>
          )}
        </div>

        <div className="pt-4 border-t border-[var(--border)]">
          <p className="text-sm text-[var(--foreground-muted)]">
            <strong>Modalidad:</strong> {study.modality} (no editable)
          </p>
          <p className="text-sm text-[var(--foreground-muted)]">
            <strong>Fecha:</strong> {new Date(study.studyDate).toLocaleDateString('es-CO')} (no editable)
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
